import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronRight,
  Bed,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select, type SelectOption } from "@/components/ui/Select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BedData {
  id: number;
  agencyId: number;
  roomName: string | null;
  bedName: string;
  siteId: number | null;
  isActive: boolean;
  notes: string | null;
  currentAssignment: {
    assignmentId: number;
    personId: number;
    assignedDate: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

interface BedsResponse {
  data: BedData[];
}

interface SiteOption {
  id: number;
  name: string;
  isActive: boolean;
}

interface SitesResponse {
  data: SiteOption[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BedManagement() {
  const queryClient = useQueryClient();
  const [siteFilter, setSiteFilter] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBed, setEditingBed] = useState<BedData | null>(null);
  const [expandedRooms, setExpandedRooms] = useState<Set<string>>(
    new Set(["__all__"])
  );

  // Form state
  const [formBedName, setFormBedName] = useState("");
  const [formRoomName, setFormRoomName] = useState("");
  const [formSiteId, setFormSiteId] = useState("");
  const [formNotes, setFormNotes] = useState("");

  // Fetch sites
  const { data: sitesData } = useQuery<SitesResponse>({
    queryKey: ["shelter-sites"],
    queryFn: () => api.get<SitesResponse>("/shelter/sites"),
  });

  const siteOptions: SelectOption[] = [
    { value: "", label: "All Sites" },
    ...(sitesData?.data ?? []).map((s) => ({
      value: String(s.id),
      label: s.name,
    })),
  ];

  const siteOptionsForForm: SelectOption[] = [
    { value: "", label: "No Site" },
    ...(sitesData?.data ?? []).map((s) => ({
      value: String(s.id),
      label: s.name,
    })),
  ];

  // Fetch beds
  const params: Record<string, string> = {};
  if (siteFilter) params.siteId = siteFilter;

  const { data: bedsData, isLoading } = useQuery<BedsResponse>({
    queryKey: ["shelter-beds", siteFilter],
    queryFn: () => api.get<BedsResponse>("/shelter/beds", { params }),
  });

  const beds = bedsData?.data ?? [];

  // Group by room
  const roomGroups = beds.reduce<Record<string, BedData[]>>((acc, bed) => {
    const room = bed.roomName || "Unassigned";
    if (!acc[room]) acc[room] = [];
    acc[room].push(bed);
    return acc;
  }, {});

  const roomNames = Object.keys(roomGroups).sort((a, b) => {
    if (a === "Unassigned") return 1;
    if (b === "Unassigned") return -1;
    return a.localeCompare(b);
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: {
      bedName: string;
      roomName?: string;
      siteId?: string;
      notes?: string;
    }) => api.post("/shelter/beds", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelter-beds"] });
      queryClient.invalidateQueries({ queryKey: ["bed-board"] });
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Record<string, unknown>;
    }) => api.put(`/shelter/beds/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelter-beds"] });
      queryClient.invalidateQueries({ queryKey: ["bed-board"] });
      resetForm();
    },
  });

  // Delete (deactivate) mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/shelter/beds/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelter-beds"] });
      queryClient.invalidateQueries({ queryKey: ["bed-board"] });
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      api.put(`/shelter/beds/${id}`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelter-beds"] });
      queryClient.invalidateQueries({ queryKey: ["bed-board"] });
    },
  });

  function resetForm() {
    setShowAddForm(false);
    setEditingBed(null);
    setFormBedName("");
    setFormRoomName("");
    setFormSiteId("");
    setFormNotes("");
  }

  function startEdit(bed: BedData) {
    setEditingBed(bed);
    setFormBedName(bed.bedName);
    setFormRoomName(bed.roomName || "");
    setFormSiteId(bed.siteId ? String(bed.siteId) : "");
    setFormNotes(bed.notes || "");
    setShowAddForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formBedName.trim()) return;

    if (editingBed) {
      updateMutation.mutate({
        id: editingBed.id,
        data: {
          bedName: formBedName.trim(),
          roomName: formRoomName.trim() || undefined,
          siteId: formSiteId || undefined,
          notes: formNotes.trim() || undefined,
        },
      });
    } else {
      createMutation.mutate({
        bedName: formBedName.trim(),
        roomName: formRoomName.trim() || undefined,
        siteId: formSiteId || undefined,
        notes: formNotes.trim() || undefined,
      });
    }
  }

  function toggleRoom(room: string) {
    setExpandedRooms((prev) => {
      const next = new Set(prev);
      if (next.has(room)) next.delete(room);
      else next.add(room);
      return next;
    });
  }

  const isAllExpanded = expandedRooms.has("__all__");

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-48">
            <Select
              options={siteOptions}
              value={siteFilter}
              onChange={setSiteFilter}
              placeholder="All Sites"
            />
          </div>
          <span className="text-sm text-muted-foreground">
            {beds.length} bed{beds.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (isAllExpanded) {
                setExpandedRooms(new Set());
              } else {
                setExpandedRooms(new Set(["__all__", ...roomNames]));
              }
            }}
          >
            {isAllExpanded ? "Collapse All" : "Expand All"}
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add Bed
          </Button>
        </div>
      </div>

      {/* Add/Edit form */}
      {showAddForm && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {editingBed ? "Edit Bed" : "Add New Bed"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Bed Name"
                  value={formBedName}
                  onChange={(e) => setFormBedName(e.target.value)}
                  placeholder="e.g., Bed 1A"
                  required
                />
                <Input
                  label="Room Name"
                  value={formRoomName}
                  onChange={(e) => setFormRoomName(e.target.value)}
                  placeholder="e.g., Room 101"
                />
                <Select
                  label="Site"
                  options={siteOptionsForForm}
                  value={formSiteId}
                  onChange={setFormSiteId}
                />
                <Input
                  label="Notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Optional notes"
                />
              </div>

              {(createMutation.isError || updateMutation.isError) && (
                <p className="text-sm text-destructive">
                  {((createMutation.error || updateMutation.error) as any)
                    ?.body?.error ?? "Failed to save bed"}
                </p>
              )}

              <div className="flex gap-2">
                <Button
                  type="submit"
                  size="sm"
                  loading={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {editingBed ? "Update Bed" : "Create Bed"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={resetForm}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && beds.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Bed className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No beds configured yet. Click "Add Bed" to get started.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Beds grouped by room */}
      {!isLoading &&
        roomNames.map((room) => {
          const isExpanded = expandedRooms.has(room) || isAllExpanded;
          const roomBeds = roomGroups[room];
          if (!roomBeds) return null;
          const activeBeds = roomBeds.filter((b) => b.isActive);
          const occupiedBeds = roomBeds.filter(
            (b) => b.currentAssignment
          );

          return (
            <Card key={room}>
              <CardHeader
                className="cursor-pointer pb-2"
                onClick={() => toggleRoom(room)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <CardTitle className="text-sm font-semibold">
                      {room}
                    </CardTitle>
                    <span className="text-xs text-muted-foreground">
                      ({occupiedBeds.length}/{activeBeds.length} occupied)
                    </span>
                  </div>
                </div>
              </CardHeader>
              {isExpanded && (
                <CardContent>
                  <div className="divide-y">
                    {roomBeds.map((bed) => (
                      <div
                        key={bed.id}
                        className={cn(
                          "flex items-center justify-between py-3 first:pt-0 last:pb-0",
                          !bed.isActive && "opacity-50"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "h-3 w-3 rounded-full",
                              !bed.isActive
                                ? "bg-gray-400"
                                : bed.currentAssignment
                                  ? "bg-blue-500"
                                  : "bg-green-500"
                            )}
                          />
                          <div>
                            <p className="text-sm font-medium">
                              {bed.bedName}
                            </p>
                            {bed.currentAssignment ? (
                              <p className="text-xs text-muted-foreground">
                                Occupied by{" "}
                                {bed.currentAssignment.firstName}{" "}
                                {bed.currentAssignment.lastName} (since{" "}
                                {new Date(
                                  bed.currentAssignment.assignedDate
                                ).toLocaleDateString()}
                                )
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                {bed.isActive ? "Available" : "Inactive"}
                              </p>
                            )}
                            {bed.notes && (
                              <p className="text-xs text-muted-foreground italic">
                                {bed.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title={
                              bed.isActive ? "Deactivate" : "Activate"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleActiveMutation.mutate({
                                id: bed.id,
                                isActive: !bed.isActive,
                              });
                            }}
                          >
                            {bed.isActive ? (
                              <ToggleRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-gray-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEdit(bed);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {!bed.currentAssignment && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  confirm(
                                    `Deactivate "${bed.bedName}"?`
                                  )
                                ) {
                                  deleteMutation.mutate(bed.id);
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
    </div>
  );
}
